import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import PropTypes from "prop-types";

const ESTATUS = { A: "Pagado", P: "Pendiente", M: "Moratorio" };
const fmtMoney = val => "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });
const fmtDate = str => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const s = StyleSheet.create({
  page: { padding: "30 40", backgroundColor: "#fff", fontSize: 9, fontFamily: "Helvetica" },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 16,
    borderBottomWidth: 2, borderBottomColor: "#1565c0", paddingBottom: 10 },
  logo: { width: 80, height: 30, objectFit: "contain" },
  headerRight: { alignItems: "flex-end" },
  title: { fontSize: 16, fontWeight: "bold", color: "#1565c0" },
  subtitle: { fontSize: 9, color: "#666", marginTop: 2 },
  contratoBox: { backgroundColor: "#e3f2fd", borderRadius: 4, padding: "6 12", marginBottom: 14,
    flexDirection: "row", justifyContent: "space-between" },
  contratoLabel: { fontSize: 9, color: "#555" },
  contratoValue: { fontSize: 11, fontWeight: "bold", color: "#1565c0" },
  summaryRow: { flexDirection: "row", gap: 12, marginBottom: 14 },
  summaryBox: { flex: 1, borderRadius: 4, padding: "6 10" },
  summaryLabel: { fontSize: 8, color: "#666", textTransform: "uppercase" },
  summaryValue: { fontSize: 12, fontWeight: "bold", marginTop: 2 },
  sectionTitle: { fontSize: 10, fontWeight: "bold", color: "#1565c0", textTransform: "uppercase",
    marginBottom: 6, marginTop: 4 },
  table: { borderWidth: 1, borderColor: "#ddd", borderRadius: 2 },
  tHead: { flexDirection: "row", backgroundColor: "#1565c0" },
  tRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee" },
  tRowAlt: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee", backgroundColor: "#f8f9fa" },
  tRowPaid: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee", backgroundColor: "#f1f8e9" },
  tRowMora: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee", backgroundColor: "#ffebee" },
  th: { padding: "4 6", fontSize: 8, color: "#fff", fontWeight: "bold", textAlign: "center" },
  td: { padding: "3 6", fontSize: 8, textAlign: "center" },
  tdRight: { padding: "3 6", fontSize: 8, textAlign: "right" },
  footer: { marginTop: 16, borderTopWidth: 1, borderTopColor: "#ddd", paddingTop: 8,
    flexDirection: "row", justifyContent: "space-between" },
  footerText: { fontSize: 7, color: "#aaa" }
});

const AmortizacionPDF = ({ data, idContrato }) => {
  const rows = data || [];
  const totalContrato = rows.reduce((s, r) => s + (parseFloat(r.MONTOAPAGAR) || 0), 0);
  const totalPagado = rows.reduce((s, r) => {
    if (r.ESTATUS === "A") return s + (parseFloat(r.MONTOAPAGAR) || 0);
    return s + (parseFloat(r.ANTICIPADO) || 0);
  }, 0);
  const totalPendiente = totalContrato - totalPagado;
  const fechaImpresion = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* HEADER */}
        <View style={s.header}>
          <Image src={require("assets/img/logotipo.png")} style={s.logo} />
          <View style={s.headerRight}>
            <Text style={s.title}>Tabla de Amortización</Text>
            <Text style={s.subtitle}>Generado el {fechaImpresion}</Text>
          </View>
        </View>

        {/* CONTRATO */}
        {idContrato && (
          <View style={s.contratoBox}>
            <Text style={s.contratoLabel}>Número de contrato</Text>
            <Text style={s.contratoValue}>{idContrato}</Text>
          </View>
        )}

        {/* RESUMEN */}
        <View style={s.summaryRow}>
          <View style={[s.summaryBox, { backgroundColor: "#e3f2fd" }]}>
            <Text style={s.summaryLabel}>Total contrato</Text>
            <Text style={[s.summaryValue, { color: "#1565c0" }]}>{fmtMoney(totalContrato)}</Text>
          </View>
          <View style={[s.summaryBox, { backgroundColor: "#e8f5e9" }]}>
            <Text style={s.summaryLabel}>Pagado</Text>
            <Text style={[s.summaryValue, { color: "#2e7d32" }]}>{fmtMoney(totalPagado)}</Text>
          </View>
          <View style={[s.summaryBox, { backgroundColor: "#fff3e0" }]}>
            <Text style={s.summaryLabel}>Pendiente</Text>
            <Text style={[s.summaryValue, { color: "#e65100" }]}>{fmtMoney(totalPendiente)}</Text>
          </View>
          <View style={[s.summaryBox, { backgroundColor: "#f5f5f5" }]}>
            <Text style={s.summaryLabel}>Parcialidades</Text>
            <Text style={[s.summaryValue, { color: "#333" }]}>{rows.length}</Text>
          </View>
        </View>

        {/* TABLA */}
        <Text style={s.sectionTitle}>Detalle de parcialidades</Text>
        <View style={s.table}>
          <View style={s.tHead}>
            <Text style={[s.th, { width: "6%" }]}>No.</Text>
            <Text style={[s.th, { width: "12%" }]}>Estatus</Text>
            <Text style={[s.th, { width: "16%" }]}>Fecha a pagar</Text>
            <Text style={[s.th, { width: "16%" }]}>Fecha de pago</Text>
            <Text style={[s.th, { width: "16%" }]}>Monto</Text>
            <Text style={[s.th, { width: "16%" }]}>Abonado</Text>
            <Text style={[s.th, { width: "18%" }]}>Por pagar</Text>
          </View>
          {rows.map((r, i) => {
            const saldo = (parseFloat(r.MONTOAPAGAR) || 0) - (parseFloat(r.ANTICIPADO) || 0);
            const rowStyle = r.ESTATUS === "A" ? s.tRowPaid : r.ESTATUS === "M" ? s.tRowMora : (i % 2 === 0 ? s.tRow : s.tRowAlt);
            return (
              <View style={rowStyle} key={i}>
                <Text style={[s.td, { width: "6%" }]}>{typeof r.ID === "number" ? r.ID + 1 : "—"}</Text>
                <Text style={[s.td, { width: "12%" }]}>{ESTATUS[r.ESTATUS] || r.ESTATUS}</Text>
                <Text style={[s.td, { width: "16%" }]}>{fmtDate(r.FECHAAPAGAR)}</Text>
                <Text style={[s.td, { width: "16%" }]}>{fmtDate(r.FECHAPAGO)}</Text>
                <Text style={[s.tdRight, { width: "16%" }]}>{fmtMoney(r.MONTOAPAGAR)}</Text>
                <Text style={[s.tdRight, { width: "16%" }]}>{parseFloat(r.ANTICIPADO) > 0 ? fmtMoney(r.ANTICIPADO) : "—"}</Text>
                <Text style={[s.tdRight, { width: "18%", fontWeight: r.ESTATUS !== "A" ? "bold" : "normal" }]}>
                  {r.ESTATUS === "A" ? (parseFloat(r.ANTICIPADO) > 0 ? "Pagado (cierre: " + fmtMoney(parseFloat(r.MONTOAPAGAR) - parseFloat(r.ANTICIPADO)) + ")" : "Pagado") : fmtMoney(saldo)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* FOOTER */}
        <View style={s.footer}>
          <Text style={s.footerText}>Documento generado automaticamente — Armenta Inmobiliaria</Text>
          {idContrato && <Text style={s.footerText}>Contrato No. {idContrato}</Text>}
        </View>
      </Page>
    </Document>
  );
};

AmortizacionPDF.propTypes = {
  data: PropTypes.array,
  idContrato: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AmortizacionPDF;
